import type { DynamicValues } from 'lib/dynamic-values/types.d'
import type { Evaluation } from 'lib/evaluation'
import type { Project } from 'lib/project'
import { getAllUuidsFromString } from 'lib/utils'
import mapDynamicFieldValueToStrings from 'ecosystems/field-editors/dynamic-string-editor/helpers/map-dynamic-field-value-to-strings'
import {
  parseJsonWithDynamicStrings,
  serializeJsonWithDynamicStrings,
} from './parser'
import type { JSONTreeDynamicValueInterface } from './types.d'

const identifier = 'com.luckymarmot.JSONTreeDynamicValue'

const defaultValue: JSONTreeDynamicValueInterface = {
  uuid: '',
  type: 'dynamicValue',
  identifier,
  json: '{}',
}

const editForm: DynamicValues.EditForm<JSONTreeDynamicValueInterface> = {
  fields: [
    {
      fieldKey: 'json',
      fieldType: 'json',
      label: 'JSON',
    },
  ],
}

const implJSONTreeDynamicValue: DynamicValues.Implementation<JSONTreeDynamicValueInterface> =
  {
    title: 'JSON',
    identifier,
    defaultValue,
    editForm,
    getAllRefs(dv) {
      // eslint-disable-next-line react/destructuring-assignment
      if (dv.json) {
        const matches = getAllUuidsFromString(dv.json)
        return matches && Array.from(new Set(matches))
      }
      return null
    },
    getEvaluatedString: async (
      dv: JSONTreeDynamicValueInterface,
      ctx: Evaluation.Ctx,
    ) => {
      const jsonStr = dv.json
      const subObjects = { ...ctx.project.objects }
      const subRoot: Project.GenericRef<Project.Project> = {
        ref: ctx.project.root?.ref || '',
      }

      if (!jsonStr || !subRoot) {
        return ''
      }

      const subCtx: Evaluation.Ctx = {
        ...ctx,
        project: {
          root: subRoot,
          objects: subObjects,
        },
      }

      try {
        const jsonTree = parseJsonWithDynamicStrings(
          subObjects,
          subRoot,
          jsonStr,
        )

        return serializeJsonWithDynamicStrings(jsonTree, async (ds) => {
          const evalutatedStrings = await ctx.evals.evaluateDynamicString(
            ds,
            subCtx,
          )
          const ret = await mapDynamicFieldValueToStrings(evalutatedStrings)

          return (
            await Promise.all(
              ret.map(async (e) =>
                typeof e === 'string'
                  ? e
                  : ctx.evals.evaluateDynamicValue(e, subCtx),
              ),
            )
          ).join('')
        })
      } catch (error) {
        // return the original string as it is
        return jsonStr
      }
    },
    getTokenInfo: async () => ({
      title: 'JSON',
      text: null,
    }),

    isEmpty: (dv: JSONTreeDynamicValueInterface) =>
      !dv.json || dv.json.length === 0 || dv.json === '{}',
  }

export default implJSONTreeDynamicValue
